import StringBuilder from 'string-builder';
import { getCustomerInfo } from '../api/lms';
import * as Sentry from '@sentry/nextjs';
import { createHash } from 'crypto';
import axios, { AxiosResponse } from 'axios';
import { CustomUrlsProps } from '@/src/app/layout';

declare var fbq;
declare const window: any;
const adminHost: string = process.env.ADMIN_HOST!;
const productRoute: string = process.env.PRODUCT_ROUTE!;
const isProd = process.env.APP_ENV === 'production';
const dfTrackingCodeUrl = process.env.DF_TRACKING_URL;
const dfAppUrl = process.env.DF_APP_URL;

export const returnToAdmin = (
  accountSlug: string,
  paymentPageId: number
): string =>
  (location.href =
    adminHost +
    productRoute
      .replace(/:account/, accountSlug)
      .replace(':id', String(paymentPageId)));

export const orderColon = (first: boolean): string =>
  first ? 'order-1' : 'order-2';

export interface IRGBA {
  r: number;
  g: number;
  b: number;
  a: number;
}

export const createRGBA = (obj: IRGBA): string =>
  `rgba(${obj.r}, ${obj.g}, ${obj.b}, ${obj.a})`;

export const getCurrentLocale = (language, namespace, loadedLocalesJSON) =>
  JSON.parse(loadedLocalesJSON)[language][namespace];

export const flattenObject = (ob) => {
  var toReturn = {};

  for (var i in ob) {
    if (!ob.hasOwnProperty(i)) continue;

    if (typeof ob[i] == 'object' && ob[i] !== null) {
      var flatObject = flattenObject(ob[i]);
      for (var x in flatObject) {
        if (!flatObject.hasOwnProperty(x)) continue;

        toReturn[i + '.' + x] = flatObject[x];
      }
    } else {
      toReturn[i] = ob[i];
    }
  }
  return toReturn;
};

export const getDeadlineFunnelTrackingCode = (userIdHash) => {
  const sb = new StringBuilder();
  sb.append(
    'function SendUrlToDeadlineFunnel(e){var r,t,c,a,h,n,o,A,i = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=",d=0,l=0,s="",u=[];if(!e)return e;do r=e.charCodeAt(d++),t=e.charCodeAt(d++),c=e.charCodeAt(d++),A=r<<16|t<<8|c,a=A>>18&63,h=A>>12&63,n=A>>6&63,o=63&A,u[l++]=i.charAt(a)+i.charAt(h)+i.charAt(n)+i.charAt(o);while(d<e.length);s=u.join("");var C=e.length%3;var decoded = (C?s.slice(0,C-3):s)+"===".slice(C||3);decoded = decoded.replace("+", "-");decoded = decoded.replace("/", "_");return decoded;} var dfUrl = SendUrlToDeadlineFunnel(location.href); var dfParentUrlValue;try {dfParentUrlValue = window.parent.location.href;} catch(err) {if(err.name === "SecurityError") {dfParentUrlValue = document.referrer;}}var dfParentUrl = (parent !== window) ? ("/" + SendUrlToDeadlineFunnel(dfParentUrlValue)) : "";'
  );
  sb.append(
    '(function() {var s = document.createElement("script");s.type = "text/javascript";s.async = true;s.setAttribute("data-scriptid", "dfunifiedcode");s.src ="'
  );
  sb.append(
    '' +
      dfTrackingCodeUrl +
      '/unified/reactunified.bundle.js?userIdHash=' +
      userIdHash +
      ''
  );
  sb.append('&pageFromUrl="+dfUrl+"&parentPageFromUrl="+dfParentUrl');
  if (!isProd) {
    sb.append('+"&baseUrl=' + dfAppUrl + '"');
  }
  sb.append(
    ';var s2 = document.getElementsByTagName("script")[0];s2.parentNode.insertBefore(s, s2);})();'
  );

  return sb.toString();
};

export const catchErrorHandler =
  (errorMessage = 'Generic error', accountName: string) =>
  (e) =>
    getCustomerInfo(accountName)
      .then((customer) => {
        Sentry.captureException(
          {
            name: 'FE SSR: ' + errorMessage,
            error: e,
          },
          { user: { email: customer.email } }
        );
      })
      .catch((e2) =>
        Sentry.captureException({
          error: e,
          customerFetchError: e2,
        })
      );

export const getGravatar = (email: string, size = 37, defaultImage = 'mp') => {
  const hash = createHash('sha256').update(email).digest('hex');
  return `https://www.gravatar.com/avatar/${hash}?s=${size}&d=${defaultImage}`;
};

interface SlugAndHostname {
  offerSlugExists: boolean | null;
  resolvedHostname: string | null;
}

export const getOfferSlugAndHostname = (
  pathname: string | null,
  hostname: string | undefined
): SlugAndHostname => {
  if (!pathname || !hostname) {
    return { offerSlugExists: null, resolvedHostname: null };
  }

  if (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.includes('vercel')
  ) {
    return {
      offerSlugExists: Boolean(pathname.split('/')[2]),
      resolvedHostname: `${pathname.split('/')[1]}.vomapagesdev.com`,
    };
  }

  const parsedUrl = new URL(window.location.href);
  return {
    offerSlugExists: Boolean(pathname.split('/')[1]),
    resolvedHostname: parsedUrl.hostname.replace(/^www\./, ''),
  };
};

export const getCustomUrls = async (host: string): Promise<CustomUrlsProps> => {
  if (!process.env.API || !host) {
    return { not_found_redirect_url: null, root_redirect_url: null };
  }

  const url = `${process.env.API}/ssr/accounts/${host}/info`;
  const headers = {
    Accept: 'application/json',
    Authorization: process.env.API_TOKEN!,
    'Content-Type': 'application/json',
  };

  try {
    const response = await axios
      .get(url, { headers })
      .then((response: AxiosResponse) => response.data);

    return {
      not_found_redirect_url: response?.data?.not_found_redirect_url,
      root_redirect_url: response?.data?.root_redirect_url,
    };
  } catch (error) {
    return { not_found_redirect_url: null, root_redirect_url: null };
  }
};
